import {
  addCommentMutation,
  deleteIssueCommentMutation,
  updateIssueCommentBodyMutation,
} from '@github-ui/server-persisted-queries'
import type {
  AddCommentInput,
  AddCommentMutationMutation,
} from '@github-ui/server-persisted-queries/types/addCommentMutation'
import type {DeleteIssueCommentMutationMutation} from '@github-ui/server-persisted-queries/types/deleteIssueCommentMutation'
import type {
  UpdateIssueCommentBodyMutationMutation,
  UpdateIssueCommentInput,
} from '@github-ui/server-persisted-queries/types/updateIssueCommentBodyMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type CommentRoute = {owner: string; repo: string; number: number}
export type ExistingCommentRoute = CommentRoute & {databaseId: number}

type GraphQLResponse<T> = {data?: T | null; errors?: ReadonlyArray<{type?: unknown; message?: unknown}> | null}

function withQueryId(url: string, queryId: string) {
  return `${url}?queryId=${encodeURIComponent(queryId)}`
}

function commentsUrl({owner, repo, number}: CommentRoute) {
  return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${number}/comments.json`
}

function commentUrl({databaseId, ...route}: ExistingCommentRoute) {
  return commentsUrl(route).replace(/\.json$/, `/${databaseId}.json`)
}

async function mutationRequest<T>(
  url: string,
  queryId: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  input?: object,
): Promise<T> {
  const response = await reactFetchJSON(withQueryId(url, queryId), {
    method,
    ...(input === undefined ? {} : {body: {input}}),
  })
  if (!response.ok) throw new Error(`Comment mutation failed (${response.status})`)

  const json = (await response.json()) as GraphQLResponse<T> | null
  const firstError = json?.errors?.[0]
  if (firstError) {
    const message = typeof firstError.message === 'string' ? firstError.message : 'Unknown error'
    const type = typeof firstError.type === 'string' ? firstError.type : undefined
    const formattedMessage = type && !message.startsWith(`${type} `) ? `${type} ${message}` : message
    throw new Error(`GraphQL error: ${formattedMessage}`)
  }
  if (!json?.data) throw new Error('Comment mutation returned no data')
  return json.data
}

export async function addCommentRequest(
  route: CommentRoute,
  input: Pick<AddCommentInput, 'body'>,
): Promise<AddCommentMutationMutation> {
  const data = await mutationRequest<AddCommentMutationMutation>(commentsUrl(route), addCommentMutation, 'POST', input)
  if (!data.addComment?.timelineEdge?.node) throw new Error('Add comment mutation returned no comment')
  return data
}

export async function deleteIssueCommentRequest(
  route: ExistingCommentRoute,
): Promise<DeleteIssueCommentMutationMutation> {
  const data = await mutationRequest<DeleteIssueCommentMutationMutation>(
    commentUrl(route),
    deleteIssueCommentMutation,
    'DELETE',
  )
  if (!data.deleteIssueComment?.issueComment) throw new Error('Delete comment mutation returned no comment')
  return data
}

export async function updateIssueCommentBodyRequest(
  route: ExistingCommentRoute,
  input: Pick<UpdateIssueCommentInput, 'body' | 'bodyVersion'>,
): Promise<UpdateIssueCommentBodyMutationMutation> {
  const data = await mutationRequest<UpdateIssueCommentBodyMutationMutation>(
    commentUrl(route),
    updateIssueCommentBodyMutation,
    'PATCH',
    input,
  )
  if (!data.updateIssueComment?.issueComment) throw new Error('Update comment mutation returned no comment')
  return data
}
